/**
 * SpeakerModelSelector Component
 *
 * This component provides a dropdown to select speaker model based on the selected mounting type.
 * It filters available models based on mounting type and is hidden when a conferencing bar is selected.
 */
import React, { useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Select from '../atoms/Select';
import InputLabel from '../atoms/InputLabel';
import { getAreaProperty, getCurrentValue } from 'reduxModules/selectors/containerSelectors';
import ReducersIndex from '../../reduxModules/ducks';
import { SpeakerMountingType } from '../../types/redux/room';
import toolIndex from '../../toolConstants';

interface SpeakerModel {
  key: string;
  modelName: string;
  mountingType: string;
}

interface SpeakerModelSelectorProps {
  show?: boolean;
  disabled?: boolean;
  label?: string;
}

const SpeakerModelSelector: React.FC<SpeakerModelSelectorProps> = ({ show = true, disabled = false, label }) => {
  const dispatch = useDispatch();

  // Get current speaker state
  const currentModel = useSelector(state => getAreaProperty(state, 'speakers.model'));
  const currentMountingType = useSelector(state =>
    getAreaProperty(state, 'speakers.mountingType') || 'ceiling'
  ) as SpeakerMountingType;

  // Check if conferencing bar is selected
  const hasBar = useSelector(state => getAreaProperty(state, 'hasBar'));
  const microphoneModel = useSelector(state => getAreaProperty(state, 'microphones.model'));
  const isBarSelected = hasBar || (microphoneModel && microphoneModel.includes('bar'));

  // Get speaker constants from tool constants
  const currentTool = useSelector(state => getCurrentValue(state, 'currentTool')) || 'conferenceRoomDesigner';
  const speakerConstants = toolIndex[currentTool]?.constants?.equipment?.speakers;

  // Filter speaker models based on mounting type
  const availableModels = useMemo(() => {
    if (!speakerConstants) return [];

    const models: SpeakerModel[] = [];

    // Skip only duplicate/generic placeholder keys
    // Keep unique models even if they use "generic-looking" key names
    const genericKeys = [
      'ceilingspkr_lp',   // Generic ceiling LP placeholder (duplicate of ceilingspkr-lp)
      'ceilingspkr_lp_taa', // Generic ceiling LP-TAA placeholder (duplicate of ceilingspkr-lp-taa)
      'ceilingspkr-mask6c', // MASK6C is a WALL speaker, not ceiling - this is an incorrect entry
      'wallspkr-dx-s8',   // Duplicate of wallspkr_dx8
      'wallspkr-ex-s6'    // Duplicate of wallspkr_ex6
      // Note: ceilingspkr (C-IC6), wallspkr (DX-S5), wallspkr-ent206 (ENT206), and wallspkr-ex-s8 (EX-S8) are unique and must be kept
    ];

    Object.entries(speakerConstants).forEach(([key, value]: [string, any]) => {
      // Skip generic keys
      if (genericKeys.includes(key)) {
        return;
      }

      // Check if this speaker matches the current mounting type
      let isMatch = false;

      if (currentMountingType === 'ceiling' && (value.isCeilingSpkr || key.startsWith('ceilingspkr'))) {
        isMatch = true;
      } else if (currentMountingType === 'pendant' && (value.isPendantSpkr || key.startsWith('pendantspkr'))) {
        isMatch = true;
      } else if (currentMountingType === 'wall' && (value.isWallSpkr || key.startsWith('wallspkr'))) {
        isMatch = true;
      }

      if (isMatch) {
        models.push({
          key,
          modelName: value.modelName || key,
          mountingType: currentMountingType
        });
      }
    });

    // Sort models alphabetically, but always put MASK6C at the bottom
    models.sort((a, b) => {
      const aIsMask = a.modelName.toUpperCase().includes('MASK6C');
      const bIsMask = b.modelName.toUpperCase().includes('MASK6C');

      // If one is MASK6C and the other isn't, MASK6C goes to bottom
      if (aIsMask && !bIsMask) return 1;
      if (!aIsMask && bIsMask) return -1;

      // If both are MASK6C or neither is MASK6C, sort alphabetically
      return a.modelName.localeCompare(b.modelName);
    });

    return models;
  }, [speakerConstants, currentMountingType]);

  // Validate that current model is in available models, otherwise use first available
  const isCurrentModelValid = currentModel && availableModels.some(m => m.key === currentModel);
  const selectedValue = isCurrentModelValid ? currentModel : (availableModels[0]?.key || '');

  // Auto-update model if current model is invalid for the mounting type
  useEffect(() => {
    if (!isBarSelected && show && availableModels.length > 0 && !isCurrentModelValid && selectedValue) {
      dispatch((ReducersIndex.container as any).setSpeakerModel(selectedValue));
    }
  }, [availableModels, isCurrentModelValid, selectedValue, currentModel, currentMountingType, dispatch, isBarSelected, show]);

  const handleChange = (event: any) => {
    const newModel = event.target.value;
    dispatch((ReducersIndex.container as any).setSpeakerModel(newModel));
  };

  // Create display names for the dropdown
  const modelBubbles = availableModels.reduce((acc, model) => {
    acc[model.key] = model.modelName;
    return acc;
  }, {} as Record<string, string>);

  // Hide selector if conferencing bar is selected
  if (isBarSelected || !show) {
    return null;
  }

  return (
    <Box className="d-flex flex-column" sx={{ width: '100%' }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={selectedValue}
        items={availableModels.map(m => m.key)}
        onChange={handleChange}
        disabled={disabled || availableModels.length === 0}
        placeHolder="Select speaker model"
        bubbles={modelBubbles}
      />
    </Box>
  );
};

export default SpeakerModelSelector;